import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExt from "@tiptap/extension-link";
import ImageExt from "@tiptap/extension-image";
import TurndownService from "turndown";
import { marked } from "marked";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Minus, Undo2, Redo2, Code2 } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { inputCls } from "./ui";

const td = new TurndownService({ headingStyle: "atx", bulletListMarker: "-", codeBlockStyle: "fenced", hr: "---" });
const toHtml = (md) => marked.parse(md || "", { async: false });

const Btn = ({ on, active, label, icon: Icon, testId }) => (
  <button type="button" onClick={on} title={label} aria-label={label} aria-pressed={!!active} data-testid={testId}
    className={`rounded p-1.5 transition-colors hover:bg-muted ${active ? "bg-muted text-pulse" : ""}`}><Icon className="h-4 w-4" /></button>
);

const MediaPicker = ({ onPick, onClose }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/media").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="media-picker">
      <div className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-card p-4" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex justify-between"><h2 className="font-semibold">Ubaci sliku iz biblioteke</h2><button type="button" onClick={onClose} data-testid="media-picker-close">Zatvori</button></div>
        {!items ? <p className="text-sm">Učitavanje…</p> : !items.length ? <p className="text-sm text-muted-foreground">Biblioteka je prazna.</p> : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {items.map((m) => (
              <button type="button" key={m.id} onClick={() => onPick(m)} className="overflow-hidden rounded border border-border text-left text-xs hover:border-pulse" data-testid={`media-pick-${m.id}`}>
                <img src={imgSrc(m.url)} alt={m.alt_text} className="aspect-[4/3] w-full object-cover" />
                <p className="truncate p-1">{m.alt_text || m.original_filename}</p>
                {!m.rights_confirmed && <p className="px-1 pb-1 text-amber-700">Prava nisu potvrđena</p>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const RichEditor = ({ value, onChange, editable = true }) => {
  const last = useRef(value);
  const [raw, setRaw] = useState(false);
  const [picker, setPicker] = useState(false);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), LinkExt.configure({ openOnClick: false, autolink: true }), ImageExt],
    content: toHtml(value),
    editable,
    editorProps: { attributes: { class: "prose-editor min-h-[420px] px-4 py-3 outline-none", "data-testid": "editor-body-rich" } },
    onUpdate: ({ editor: ed }) => {
      const md = td.turndown(ed.getHTML());
      last.current = md;
      onChange(md);
    },
  });
  useEffect(() => {
    if (editor && value !== last.current) {
      last.current = value;
      editor.commands.setContent(toHtml(value), false);
    }
  }, [value, editor]);
  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable, false);
  }, [editable, editor]);
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const link = () => {
    const url = window.prompt("URL linka (https://…)", editor.getAttributes("link").href || "");
    if (url === null) return;
    if (!url) c().unsetLink().run();
    else if (/^(https?:|mailto:)/.test(url)) c().extendMarkRange("link").setLink({ href: url }).run();
  };
  return (
    <div className="rounded-md border border-input bg-card" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1" role="toolbar" aria-label="Formatiranje">
        {!raw && <>
          <Btn on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Podnaslov (H2)" icon={Heading2} testId="rte-h2" />
          <Btn on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Manji podnaslov (H3)" icon={Heading3} testId="rte-h3" />
          <Btn on={() => c().toggleBold().run()} active={editor.isActive("bold")} label="Podebljano" icon={Bold} testId="rte-bold" />
          <Btn on={() => c().toggleItalic().run()} active={editor.isActive("italic")} label="Kurziv" icon={Italic} testId="rte-italic" />
          <Btn on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Lista" icon={List} testId="rte-ul" />
          <Btn on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Numerisana lista" icon={ListOrdered} testId="rte-ol" />
          <Btn on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Citat" icon={Quote} testId="rte-quote" />
          <Btn on={link} active={editor.isActive("link")} label="Link" icon={Link2} testId="rte-link" />
          <Btn on={() => setPicker(true)} label="Slika iz biblioteke" icon={ImagePlus} testId="rte-image" />
          <Btn on={() => c().setHorizontalRule().run()} label="Razdjelnik" icon={Minus} testId="rte-hr" />
          <Btn on={() => c().undo().run()} label="Poništi" icon={Undo2} testId="rte-undo" />
          <Btn on={() => c().redo().run()} label="Ponovi" icon={Redo2} testId="rte-redo" />
        </>}
        <button type="button" onClick={() => setRaw(!raw)} className="ml-auto inline-flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-muted" data-testid="rte-toggle-markdown"><Code2 className="h-3.5 w-3.5" /> {raw ? "Vizuelni prikaz" : "Markdown"}</button>
      </div>
      {raw ? <textarea id="e-body" rows={18} value={value} readOnly={!editable} onChange={(e) => onChange(e.target.value)} className={`${inputCls} rounded-none border-0 font-mono text-[13px]`} data-testid="editor-body-input" />
        : <EditorContent editor={editor} />}
      {picker && <MediaPicker onClose={() => setPicker(false)} onPick={(m) => { c().setImage({ src: m.url, alt: m.alt_text || "" }).run(); setPicker(false); }} />}
    </div>
  );
};
